/*
 * The Frosted Muzzle — system configurator (Phase 4 / Mobility System Page).
 *
 * A custom element (<fm-system-configurator>) that reads a rendered fm_system
 * metaobject (inlined as JSON by system-configurator.liquid), lets the customer
 * pick dog size + supply duration + subscribe-and-save + dog name, and resolves
 * the selection to a single composite /cart/add.js call: one line item per
 * component product with the matrix-resolved quantity and the line-item
 * properties that group the system in the cart drawer.
 *
 * It owns no analytics logic of its own — it dispatches `fm:analytics`
 * CustomEvents that assets/analytics.js consumes. Cross-section updates (the
 * system-component-list section) go through the `fm:system:configured` event,
 * never by reaching into another section's DOM.
 */
(function () {
  'use strict';

  var DEFAULT_PROPS = ['_system', '_system_size', '_system_duration', '_system_subscribed'];

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function track(event, payload) {
    emit('fm:analytics', { event: event, payload: payload });
  }

  class SystemConfigurator extends HTMLElement {
    constructor() {
      super();
      this.state = { size: null, duration: null, subscribed: false, dogName: '' };
    }

    connectedCallback() {
      var dataEl = this.querySelector('[data-fm-system]');
      if (!dataEl) return;
      try {
        this.system = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.warn('[fm] system configurator: invalid system JSON', e);
        return;
      }

      var sizes = this.system.sizes || [];
      var durations = (this.system.durations || []).map(String);
      if (!sizes.length || !durations.length) return;

      // Defaults: configured defaults, else middle option (a “most popular” bias).
      this.state.size = this.system.default_size || sizes[Math.floor(sizes.length / 2)] || sizes[0];
      this.state.duration = String(
        this.system.default_duration || durations[Math.floor(durations.length / 2)] || durations[0]
      );

      this.priceEl = this.querySelector('[data-fm-price]');
      this.comparePriceEl = this.querySelector('[data-fm-compare-price]');
      this.savingsEl = this.querySelector('[data-fm-savings]');
      this.summaryEl = this.querySelector('[data-fm-bundle-summary]');
      this.addButton = this.querySelector('[data-fm-add]');
      this.dogNameInput = this.querySelector('[data-fm-dog-name]');
      this.errorEl = this.querySelector('[data-fm-error]');

      this.bindControls();
      this.render();

      track('view_item', {
        system_handle: this.system.handle,
        default_size: this.state.size,
        default_duration: Number(this.state.duration),
        default_price: this.currentPrice(),
        currency: this.system.currency
      });
    }

    bindControls() {
      var self = this;

      this.querySelectorAll('[data-fm-size]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.state.size = el.getAttribute('data-fm-size');
          self.render();
          track('system_configure', {
            system_handle: self.system.handle,
            option_name: 'size',
            option_value: self.state.size
          });
        });
      });

      this.querySelectorAll('[data-fm-duration]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.state.duration = el.getAttribute('data-fm-duration');
          self.render();
          track('system_configure', {
            system_handle: self.system.handle,
            option_name: 'duration',
            option_value: self.state.duration
          });
        });
      });

      var subToggle = this.querySelector('[data-fm-subscribe]');
      if (subToggle) {
        subToggle.addEventListener('click', function () {
          self.state.subscribed = !self.state.subscribed;
          subToggle.setAttribute('aria-pressed', self.state.subscribed ? 'true' : 'false');
          self.render();
          track('subscribe_toggle', {
            system_handle: self.system.handle,
            enabled: self.state.subscribed
          });
        });
      }

      if (this.dogNameInput) {
        this.dogNameInput.addEventListener('input', function () {
          self.state.dogName = self.dogNameInput.value.trim();
          self.updateAddButton();
        });
        this.dogNameInput.addEventListener('blur', function () {
          // Never log the name itself — only whether one was supplied.
          track('dog_name_entered', {
            system_handle: self.system.handle,
            has_value: self.state.dogName.length > 0
          });
        });
      }

      if (this.addButton) {
        this.addButton.addEventListener('click', function (e) {
          e.preventDefault();
          self.addToCart();
        });
      }
    }

    /* ---- pricing ---------------------------------------------------------- */

    basePrice() {
      var matrix = this.system.pricing_matrix || {};
      var row = matrix[this.state.size] || {};
      return Number(row[this.state.duration] || 0); // cents
    }

    currentPrice() {
      var price = this.basePrice();
      if (this.state.subscribed && this.system.subscription_discount_pct) {
        price = Math.round(price * (100 - this.system.subscription_discount_pct) / 100);
      }
      return price;
    }

    formatMoney(cents) {
      var amount = (cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.system.currency || 'USD'
        }).format(amount);
      } catch (e) {
        return '$' + amount.toFixed(2);
      }
    }

    /* ---- resolve the bundle ---------------------------------------------- */

    resolvedComponents() {
      var size = this.state.size;
      var duration = this.state.duration;
      return (this.system.components || [])
        .map(function (c) {
          var matrix = c.quantity_matrix || {};
          var row = matrix[size] || {};
          var qty = Number(row[duration] || 0);
          return {
            variant_id: c.variant_id,
            product_id: c.product_id,
            title: c.title,
            kind: c.kind || 'supplement',
            available: c.available !== false,
            selling_plan_ids: c.selling_plan_ids || null,
            quantity: qty
          };
        })
        .filter(function (c) {
          return c.quantity > 0;
        });
    }

    outOfStockComponent() {
      var miss = this.resolvedComponents().filter(function (c) {
        return !c.available || !c.variant_id;
      });
      return miss.length ? miss[0] : null;
    }

    /* ---- render ----------------------------------------------------------- */

    render() {
      // Selected-state styling on the segmented controls.
      this.querySelectorAll('[data-fm-size]').forEach((el) => {
        var on = el.getAttribute('data-fm-size') === this.state.size;
        el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.querySelectorAll('[data-fm-duration]').forEach((el) => {
        var on = el.getAttribute('data-fm-duration') === this.state.duration;
        el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      var price = this.currentPrice();
      if (this.priceEl) this.priceEl.textContent = this.formatMoney(price);

      if (this.comparePriceEl) {
        var base = this.basePrice();
        var showCompare = this.state.subscribed && base > price;
        this.comparePriceEl.textContent = showCompare ? this.formatMoney(base) : '';
        this.comparePriceEl.hidden = !showCompare;
      }

      if (this.savingsEl) {
        var saved = this.basePrice() - price;
        this.savingsEl.hidden = saved <= 0;
        if (saved > 0) {
          this.savingsEl.textContent = this.savingsEl.getAttribute('data-fm-savings-label')
            ? this.savingsEl.getAttribute('data-fm-savings-label').replace('[amount]', this.formatMoney(saved))
            : this.formatMoney(saved);
        }
      }

      // Autoship-only trust signals (e.g. free shipping) — see the
      // subscribe_only block setting in system-configurator.liquid.
      this.querySelectorAll('[data-fm-trust-subscribe-only]').forEach((el) => {
        el.hidden = !this.state.subscribed;
      });

      this.renderSummary();
      this.updateAddButton();

      // Let the system-component-list section update itself (no DOM reach-in).
      emit('fm:system:configured', {
        system_handle: this.system.handle,
        size: this.state.size,
        duration: this.state.duration,
        subscribed: this.state.subscribed,
        price: price,
        components: this.resolvedComponents()
      });
    }

    renderSummary() {
      if (!this.summaryEl) return;
      var items = this.resolvedComponents();
      if (!items.length) {
        this.summaryEl.innerHTML = '';
        return;
      }
      this.summaryEl.innerHTML = items
        .map(function (c) {
          var digital = c.kind === 'pdf';
          return (
            '<li class="fm-configurator__summary-item' +
            (digital ? ' fm-configurator__summary-item--digital' : '') +
            '"><span class="fm-configurator__summary-qty">' +
            c.quantity +
            '×</span> <span>' +
            (c.title || '') +
            '</span></li>'
          );
        })
        .join('');
    }

    updateAddButton() {
      if (!this.addButton) return;
      var oos = this.outOfStockComponent();
      var needsName = this.requiresDogName() && !this.state.dogName;
      var disabled = !!oos || needsName || this.resolvedComponents().length === 0;

      this.addButton.disabled = disabled;

      if (this.errorEl) {
        if (oos) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = (this.errorEl.getAttribute('data-fm-oos-label') || 'Out of stock: [item]').replace(
            '[item]',
            oos.title || ''
          );
        } else {
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
        }
      }
    }

    requiresDogName() {
      return this.getAttribute('data-require-dog-name') !== 'false';
    }

    /* ---- composite add-to-cart ------------------------------------------- */

    buildItems() {
      var props = {
        _system: this.system.handle,
        _system_size: this.state.size,
        _system_duration: String(this.state.duration),
        _system_subscribed: this.state.subscribed ? 'true' : 'false'
      };
      if (this.state.dogName) props._dog_name = this.state.dogName;

      // Per-duration selling plans. Each supplement product has its own
      // Recharge selling-plan group, so the plan id is product-specific:
      // prefer the component's own duration->plan map, then a system-level
      // map, then a single plan id (single-cadence / shared-group systems).
      var duration = String(this.state.duration);
      var systemMap = this.system.selling_plan_ids;
      var systemPlan = (systemMap && systemMap[duration]) || this.system.selling_plan_id;
      var subscribed = this.state.subscribed;

      return this.resolvedComponents().map(function (c) {
        var item = {
          id: c.variant_id,
          quantity: c.quantity,
          properties: Object.assign({ _system_component_kind: c.kind }, props)
        };
        // PDFs are never subscriptions; only supplement lines get a selling plan.
        if (subscribed && c.kind === 'supplement') {
          var compMap = c.selling_plan_ids;
          var plan = (compMap && compMap[duration]) || systemPlan;
          if (plan) item.selling_plan = plan;
        }
        return item;
      });
    }

    async addToCart() {
      // Disable-on-inflight: ignore repeat clicks until the current composite
      // add resolves. Without this, rapid clicks fire multiple /cart/add.js
      // calls and duplicate the whole system in the cart (the double-add bug).
      if (this._inflight) return;
      var oos = this.outOfStockComponent();
      if (oos) return;
      var items = this.buildItems();
      if (!items.length) return;

      this._inflight = true;
      this.setLoading(true);

      var cartDrawer = document.querySelector('cart-drawer');
      var sections = cartDrawer && cartDrawer.getSectionsToRender
        ? cartDrawer.getSectionsToRender().map(function (s) { return s.id; })
        : [];

      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: items,
            sections: sections,
            sections_url: window.location.pathname
          })
        });
        var data = await res.json();
        if (!res.ok) throw new Error(data.description || 'cart add failed');

        track('add_to_cart', {
          system_handle: this.system.handle,
          size: this.state.size,
          duration: Number(this.state.duration),
          is_subscription: this.state.subscribed,
          total_price: this.currentPrice(),
          currency: this.system.currency,
          component_count: items.length
        });

        // Refresh + open the cart drawer the Dawn way. renderContents()
        // re-renders the drawer contents and open() makes it visible.
        // (The trapFocus null crash this used to cause on the transitionend
        // is guarded in cart-drawer.js, so the explicit open is safe.)
        if (cartDrawer && data.sections) {
          if (cartDrawer.renderContents) cartDrawer.renderContents(data);
          // If the page loaded with an empty cart, <cart-drawer> keeps its
          // is-empty class (renderContents only clears it from
          // .drawer__inner), which hides the freshly injected lines. The
          // cart is non-empty after a successful add, so clear it here too.
          cartDrawer.classList.remove('is-empty');
          if (cartDrawer.open) cartDrawer.open();
        }
        // Reconcile against /cart.js so a multi-line composite add can't leave
        // any subscribed section out of sync (reconcile wins on conflict).
        if (typeof window.reconcileCart === 'function') {
          window.reconcileCart();
        } else if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'fm-system-configurator',
            productVariantId: items[0] && items[0].id
          });
        }

        // Hand off to the Phase 6 gift modal (it listens for this).
        emit('fm:system:added', {
          system_handle: this.system.handle,
          dog_name: this.state.dogName
        });
      } catch (err) {
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = err.message;
        }
      } finally {
        this._inflight = false;
        this.setLoading(false);
      }
    }

    setLoading(on) {
      if (!this.addButton) return;
      this.addButton.classList.toggle('fm-configurator__add--loading', on);
      this.addButton.setAttribute('aria-busy', on ? 'true' : 'false');
    }
  }

  if (!customElements.get('fm-system-configurator')) {
    customElements.define('fm-system-configurator', SystemConfigurator);
  }
})();
